import React, { useState, useEffect } from 'react';
import { API } from './App';

// Which Benchmark Period a game is measured against. The values themselves were
// worked out and written down when the period closed, so choosing one here is a
// lookup, not a recalculation. Tests already dealt keep the benchmark they were
// measured against, so changing this never moves a review that has been given out.

const styles = {
  box: {
    backgroundColor: '#16213e',
    border: '1px solid #0f3460',
    borderRadius: '12px',
    padding: '16px 20px',
    marginBottom: '24px',
  },
  row: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' },
  label: {
    color: '#a0a0b0', fontSize: '12px', letterSpacing: '1px',
    textTransform: 'uppercase', fontWeight: 'bold',
  },
  select: {
    padding: '10px 12px', backgroundColor: '#0f3460', border: '1px solid #0f3460',
    borderRadius: '8px', color: '#ffffff', fontSize: '14px', outline: 'none',
    minWidth: '260px', colorScheme: 'dark',
  },
  inUse: { color: '#ffffff', fontSize: '15px', fontWeight: 'bold' },
  note: { color: '#a0a0b0', fontSize: '12px', marginTop: '10px', lineHeight: 1.6 },
  saved: { color: '#00cc00', fontSize: '13px' },
  error: { color: '#e94560', fontSize: '13px' },
};

function BenchmarkPicker({ gameTypeId, canEdit, onChange }) {
  const token = localStorage.getItem('admin_token');
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!gameTypeId) return;
    setSaved('');
    setError('');
    fetch(`${API}/admin/games/${gameTypeId}/benchmark-periods`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => setData(d))
      .catch(() => setError('Could not load this game’s benchmarks'));
  }, [gameTypeId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function choose(periodId) {
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${API}/admin/games/${gameTypeId}/active-benchmark`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ period_id: periodId || null }),
      });
      const d = await res.json();
      if (res.ok) {
        setData(prev => ({ ...prev, active_period_id: periodId || null, in_use_label: d.in_use_label }));
        setSaved(`Now reporting against ${d.in_use_label || 'live averages'}`);
        setTimeout(() => setSaved(''), 4000);
        if (onChange) onChange(periodId || null);
      } else {
        setError(typeof d.detail === 'string' ? d.detail : 'Could not change the benchmark');
      }
    } catch (e) {
      setError('Could not connect to server');
    }
    setBusy(false);
  }

  const choices = data?.choices || [];

  if (data && choices.length === 0) {
    return (
      <div style={styles.box}>
        <div style={styles.label}>Benchmark in use</div>
        <div style={{ ...styles.inUse, marginTop: '6px' }}>Running averages</div>
        <div style={styles.note}>
          No Benchmark Period has been closed for this game yet, so the figures below are averages of every
          test recorded so far, and they move as tests come in. Run a Benchmark Period from Games &amp;
          Benchmarks to fix them.
        </div>
      </div>
    );
  }

  return (
    <div style={styles.box}>
      <div style={styles.row}>
        <div>
          <div style={styles.label}>Benchmark in use</div>
          <div style={{ ...styles.inUse, marginTop: '6px' }}>
            {data ? (data.in_use_label || 'Running averages') : 'Loading...'}
          </div>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <div style={styles.label}>Report against</div>
          <select
            style={{ ...styles.select, marginTop: '6px', opacity: canEdit ? 1 : 0.6 }}
            value={data?.active_period_id || ''}
            disabled={!canEdit || busy || !data}
            onChange={e => choose(e.target.value)}
          >
            <option value="">Most recent benchmark (automatic)</option>
            {choices.map(c => (
              <option key={c.id} value={c.id}>
                {c.label}
                {c.closed_at ? ` — closed ${new Date(c.closed_at).toLocaleDateString()}` : ''}
              </option>
            ))}
          </select>
        </div>
      </div>
      {saved && <div style={{ ...styles.saved, marginTop: '10px' }}>{saved}</div>}
      {error && <div style={{ ...styles.error, marginTop: '10px' }}>{error}</div>}
      <div style={styles.note}>
        These values were worked out and stored when the period closed — they do not change.
        Tests already dealt keep the benchmark they were measured against, so past reviews read the same
        as when they were printed; this sets what new tests are compared with.
        {data?.active_period_set_by && (
          <> Chosen by {data.active_period_set_by}
            {data.active_period_set_at
              ? ` on ${new Date(data.active_period_set_at).toLocaleDateString()}`
              : ''}.
          </>
        )}
      </div>
    </div>
  );
}

export default BenchmarkPicker;
